/** Plancher du prestataire de paiement fractionné, en centimes (50 €). */
export const INSTALLMENT_PAYMENT_MIN_AMOUNT_CENTS = 5000;
/**
 * Plafond du prestataire de paiement fractionné, en centimes (6 000 €).
 *
 * Le flux hors tunnel ne le vérifie plus : c'est le prestataire qui accepte ou refuse le
 * crédit. La constante reste la référence des bornes affichées dans le tunnel de checkout
 * (packages/features/src/constants/installments-constants.ts).
 */
export const INSTALLMENT_PAYMENT_MAX_AMOUNT_CENTS = 600000;
/**
 * Motifs de refus du paiement en plusieurs fois sur un dossier existant.
 *
 * Source de vérité unique : le type en est DÉRIVÉ, et le serveur comme le front lisent ce
 * même tableau. Un motif ajouté ici est donc automatiquement connu des deux côtés — une
 * liste recopiée à la main ne lèverait aucune erreur de compilation en oubliant une valeur,
 * et le front retomberait silencieusement sur son message générique.
 *
 * L'ordre d'évaluation, lui, est figé dans {@link checkInstallmentPaymentEligibility} : le
 * front n'affiche que le premier motif rencontré.
 *
 * Volontairement réduit au strict minimum : la date de départ et le plafond de montant sont
 * l'affaire du prestataire (FLOA reçoit et score les dates de voyage, Oney/SSP tranche sur
 * le dossier de crédit). Nous ne refusons nous-mêmes que ce que le prestataire ne peut pas
 * savoir : l'état du dossier chez nous.
 */
export const INSTALLMENT_PAYMENT_INELIGIBILITY_REASONS = [
    /** Solde restant <= 0 : il n'y a plus rien à régler. */
    'ALREADY_SETTLED',
    /** Solde restant sous le plancher prestataire. */
    'AMOUNT_TOO_LOW',
    'BOOKING_CANCELLED',
    /** Un règlement 3/4x est déjà encaissé sur le dossier : pas de cumul. */
    'INSTALLMENT_ALREADY_USED',
];
/**
 * Décide si le solde d'un dossier existant peut être réglé en 3 ou 4 fois.
 *
 * Fonction pure, partagée entre le serveur (qui l'impose) et le front (qui affiche le motif
 * de refus) : les deux doivent trancher exactement pareil, sinon le client voit un parcours
 * que le serveur refusera ensuite.
 *
 * Le contrat PDF, lui, ne l'appelle pas : le bouton 3/4x y est inconditionnel, et c'est le
 * formulaire /payer-acompte qui porte le refus — avec son message — au moment du clic.
 *
 * L'ordre des refus est significatif — le front ne montre que le premier motif, on va donc du
 * plus structurant (dossier annulé) au plus circonstanciel (montant sous le plancher).
 */
export function checkInstallmentPaymentEligibility(input) {
    if (input.isCancelled)
        return { eligible: false, reason: 'BOOKING_CANCELLED' };
    if (input.hasSettledInstallmentPayment) {
        return { eligible: false, reason: 'INSTALLMENT_ALREADY_USED' };
    }
    const remainingCents = input.totalPriceCents - input.paidCents;
    if (remainingCents <= 0)
        return { eligible: false, reason: 'ALREADY_SETTLED' };
    if (remainingCents < INSTALLMENT_PAYMENT_MIN_AMOUNT_CENTS) {
        return { eligible: false, reason: 'AMOUNT_TOO_LOW' };
    }
    return { amountCents: remainingCents, eligible: true };
}
/**
 * Libellé client d'un refus de paiement fractionné.
 *
 * Centralisé ici pour que l'espace client et le tunnel de règlement expliquent le refus
 * avec les mêmes mots.
 */
export function getInstallmentPaymentIneligibilityMessage(reason) {
    switch (reason) {
        case 'ALREADY_SETTLED':
            return 'Ce dossier est déjà entièrement réglé.';
        case 'AMOUNT_TOO_LOW':
            return `Le paiement en 3 ou 4 fois n'est possible qu'à partir de ${formatEuros(INSTALLMENT_PAYMENT_MIN_AMOUNT_CENTS)}. Le solde de votre dossier est inférieur à ce montant.`;
        case 'BOOKING_CANCELLED':
            return 'Ce dossier a été annulé.';
        case 'INSTALLMENT_ALREADY_USED':
            return 'Un paiement en plusieurs fois est déjà en place sur ce dossier.';
    }
}
/** Garde de type sur une valeur venue du réseau (corps d'erreur HTTP, query string…). */
export function isInstallmentPaymentIneligibilityReason(value) {
    return (typeof value === 'string' &&
        INSTALLMENT_PAYMENT_INELIGIBILITY_REASONS.includes(value));
}
const eurosFormatter = new Intl.NumberFormat('fr-FR', {
    currency: 'EUR',
    maximumFractionDigits: 0,
    style: 'currency',
});
/** Formate un montant en centimes pour un message client, ex: 600_000 -> "6 000 €". */
function formatEuros(cents) {
    return eurosFormatter.format(cents / 100);
}
